import { Component, ViewChild, ElementRef, ChangeDetectorRef, AfterViewChecked, EventEmitter, Output, HostListener } from '@angular/core';
import { FunctionsService } from 'src/app/data/functions.service';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-fullscreen-modal-camera',
  templateUrl: './fullscreen-modal-camera.component.html',
  styleUrls: ['./fullscreen-modal-camera.component.css']
})
export class FullscreenModalCameraComponent implements AfterViewChecked {
  selectedFile: File | null = null;
  imageType: 'webp' | 'png' | 'jpg' | 'jpeg' | null = null;
  error: string | null = null;
  result: any = null;
  loading = false;
  cameraActive = false;
  stream: MediaStream | null = null;
  imagePreviewUrl: SafeUrl | null = null;
  isOpen = true; // Control modal visibility
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;
  private videoElementReady = false;
  @Output() closed = new EventEmitter<void>(); // Emit event when modal is closed

  constructor(
    private functions: FunctionsService,
    public dialogRef: MatDialogRef<FullscreenModalCameraComponent>,
    private router: Router,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) {
    this.dialogRef.updateSize('100vw', '100vh');
  }

  ngAfterViewChecked(): void {
    if (this.cameraActive && this.videoElement && this.videoElement.nativeElement && !this.videoElementReady) {
      this.videoElementReady = true;
      this.cdr.detectChanges();
    }
  }

  truncateFileName(name: string): string {
    const [base, ext] = name.split('.').reduce((acc, part, i, arr) => {
      return i === arr.length - 1 ? [acc[0], part] : [acc[0] + part + '.', ''];
    }, ['', '']);
    return base.length > 10 ? `${base.substring(0, 10)}...${ext}` : name;
  }

  async openCamera(): Promise<void> {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      this.cameraActive = true;
      this.selectedFile = null;
      this.imagePreviewUrl = null;

      // Wait for the video element to be ready
      await this.waitForVideoElement();
      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = this.stream;
      } else {
        throw new Error('Video element not found after waiting');
      }
    } catch (err) {
      this.error = 'Falha ao acessar a câmera. Por favor, permita o acesso à câmera ou verifique se o dispositivo tem uma câmera disponível.';
      console.error('Camera access failed:', err);
      this.stopCamera();
    }
  }

  private waitForVideoElement(): Promise<void> {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (this.videoElement && this.videoElement.nativeElement && this.videoElementReady) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 50);
      // Timeout after 2 seconds to prevent infinite waiting
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Video element not found within timeout'));
      }, 2000);
    });
  }

  capturePhoto(): void {
    if (!this.videoElement || !this.videoElement.nativeElement) {
      this.error = 'Elemento de vídeo não disponível';
      return;
    }
    const video = this.videoElement.nativeElement;
    const canvas = document.getElementById('canvas') as HTMLCanvasElement;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      canvas.toBlob((blob) => {
        if (blob) {
          this.selectedFile = new File([blob], `photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
          this.imageType = 'jpg';
          this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.selectedFile));
          this.stopCamera();
        }
      }, 'image/jpeg');
    }
  }

  stopCamera(): void {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop());
      this.stream = null;
      this.cameraActive = false;
      this.videoElementReady = false;
      this.dialogRef.updateSize('auto', 'auto');
    }
  }

  onFileSelected(event: Event): void {
    // Stop camera if active
    if (this.cameraActive) {
      this.stopCamera();
    }
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      const extension = this.selectedFile.name.split('.').pop()?.toLowerCase();
      if (extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'webp') {
        this.imageType = extension === 'jpeg' ? 'jpg' : extension as 'webp' | 'png' | 'jpg';
        this.error = null;
        this.imagePreviewUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.selectedFile));
      } else {
        this.error = 'Tipo de arquivo inválido. Por favor, selecione uma imagem .jpg, .png ou .webp.';
        this.selectedFile = null;
        this.imageType = null;
        this.imagePreviewUrl = null;
      }
    }
  }

  identifyProduct(): void {
    if (!this.selectedFile || !this.imageType) {
      this.error = 'Por favor, selecione uma imagem válida.';
      return;
    }
    this.loading = true;
    this.error = null;
    this.result = null;
    let link = null;
    this.functions.identifyProduct(this.selectedFile, this.imageType).subscribe({
      next: (response) => {
        const { ean_code, product_name } = response.data;
        this.functions.produto(ean_code).subscribe({
          next: (response) => {
            const { data } = response.body;
            link = `/produto/${data.ean}/${data.nome.replace(/\s/g, '-').replace(/\//g, '')}.html`;
            this.onCancel();
            this.router.navigate([link], { skipLocationChange: false, replaceUrl: true }).then(() => {
              window.location.reload();
            }).catch(console.error)
          },
          error: (err) => {
            this.result = 'Ampliando a Busca...';
            this.onCancel();
            this.router.navigateByUrl(`/produtos/${product_name}`).then(() => {
              window.location.reload();
            }).catch(console.error);
          }
        })
      },
      error: (err) => {
        this.error = `Erro: ${err.message || err.statusText}`;
        console.error('Request failed:', err);
      },
    });
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  }

  onCancel(): void {
    this.stopCamera();
    this.dialogRef.close();
  }

  closeModal(): void {
    this.stopCamera();
    this.isOpen = false;
    this.closed.emit();
    this.dialogRef.close();
  }


  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    this.closeModal();
  }
}
